import { useEffect, useRef } from 'react';
import { clipboardHasFilePayload, readClipboardImageFiles } from '../clipboardImages';

export type PasteNotifySeverity = 'success' | 'error' | 'warning' | 'info';

interface UsePasteReferenceImagesOptions {
  enabled: boolean;
  remainingSlots: number;
  blockedReason?: string;
  onAddFiles: (files: File[]) => void | Promise<void>;
  onNotify: (message: string, severity: PasteNotifySeverity) => void;
}

export function usePasteReferenceImages({
  enabled,
  remainingSlots,
  blockedReason,
  onAddFiles,
  onNotify,
}: UsePasteReferenceImagesOptions): void {
  const onAddFilesRef = useRef(onAddFiles);
  const onNotifyRef = useRef(onNotify);
  const remainingSlotsRef = useRef(remainingSlots);
  const blockedReasonRef = useRef(blockedReason);

  onAddFilesRef.current = onAddFiles;
  onNotifyRef.current = onNotify;
  remainingSlotsRef.current = remainingSlots;
  blockedReasonRef.current = blockedReason;

  useEffect(() => {
    if (!enabled) return undefined;

    const onPaste: EventListener = (event) => {
      const clipboardData = (event as ClipboardEvent).clipboardData;
      const images = readClipboardImageFiles(clipboardData);
      if (images.length === 0) {
        if (clipboardHasFilePayload(clipboardData)) {
          event.preventDefault();
          onNotifyRef.current('Kunde inte klistra in bilden.', 'error');
        }
        return;
      }

      event.preventDefault();

      if (blockedReasonRef.current) {
        onNotifyRef.current(blockedReasonRef.current, 'warning');
        return;
      }

      const remaining = remainingSlotsRef.current;
      if (remaining <= 0) {
        onNotifyRef.current('Max antal referenser nått.', 'warning');
        return;
      }

      const toAdd = images.slice(0, remaining);
      void Promise.resolve(onAddFilesRef.current(toAdd)).catch(() => {
        onNotifyRef.current('Kunde inte klistra in bilden.', 'error');
      });

      if (images.length > remaining) {
        onNotifyRef.current('Max antal referenser nått.', 'warning');
      }
    };

    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('paste', onPaste);
    };
  }, [enabled]);
}
